/**
 * GameCanvas.tsx - React component wrapper for the game engine
 * Manages canvas lifecycle and integrates with React
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { GameEngine } from './GameEngine';
import { GameScene } from './types/engine';

interface GameCanvasProps {
  width?: number;
  height?: number;
  scale?: number;
  className?: string;
  onEngineReady?: (engine: GameEngine) => void;
}

export const GameCanvas: React.FC<GameCanvasProps> = ({
  width = 256,
  height = 240,
  scale = 3,
  className = '',
  onEngineReady,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<GameEngine | null>(null);
  const [isReady, setIsReady] = useState(false);

  // Store callback in ref to avoid dependency issues
  const onEngineReadyRef = useRef(onEngineReady);
  onEngineReadyRef.current = onEngineReady;

  // Track if we've already initialized
  const initializedRef = useRef(false);

  useEffect(() => {
    // Only initialize once
    if (initializedRef.current) return;
    if (!canvasRef.current) return;

    initializedRef.current = true;

    // Initialize engine
    const engine = new GameEngine(canvasRef.current, {
      baseWidth: width,
      baseHeight: height,
      scale,
      targetFPS: 60,
      pixelPerfect: true,
    });

    engineRef.current = engine;

    // Notify parent component FIRST so they can register scenes
    console.log('[GameCanvas] Calling onEngineReady callback...');
    if (onEngineReadyRef.current) {
      onEngineReadyRef.current(engine);
      console.log('[GameCanvas] onEngineReady callback completed');
    } else {
      console.log('[GameCanvas] No onEngineReady callback!');
    }

    // Start engine AFTER scenes are registered
    console.log('[GameCanvas] Starting engine...');
    engine.start();
    console.log('[GameCanvas] Engine started, currentScene:', engine.getCurrentScene());
    setIsReady(true);

    // Cleanup
    return () => {
      console.log('[GameCanvas] Cleanup - destroying engine');
      engine.destroy();
      engineRef.current = null;
      initializedRef.current = false;
    };
  }, [width, height, scale]); // Removed onEngineReady - use ref instead

  // Handle window resize to maintain aspect ratio
  useEffect(() => {
    const handleResize = () => {
      if (!canvasRef.current) return;

      const container = canvasRef.current.parentElement;
      if (!container) return;

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      const aspectRatio = width / height;
      let newScale = scale;

      // Calculate maximum scale that fits container
      if (containerWidth / containerHeight > aspectRatio) {
        // Width is limiting factor
        newScale = Math.floor(containerHeight / height);
      } else {
        // Height is limiting factor
        newScale = Math.floor(containerWidth / width);
      }

      // Update canvas style
      canvasRef.current.style.width = `${width * newScale}px`;
      canvasRef.current.style.height = `${height * newScale}px`;
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial call

    return () => window.removeEventListener('resize', handleResize);
  }, [width, height, scale]);

  return (
    <div
      className={`game-canvas-container ${className}`}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        backgroundColor: '#000',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          imageRendering: 'pixelated',
        }}
      />

      {/* Loading overlay */}
      {!isReady && (
        <div
          style={{
            position: 'absolute',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: '#ffffff',
            fontFamily: 'monospace',
            fontSize: '16px',
          }}
        >
          Initializing...
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Hook for accessing engine from parent components
// ============================================================================

export const useGameEngine = () => {
  const [engine, setEngine] = useState<GameEngine | null>(null);

  // Memoize the callback to prevent it from changing on every render
  const handleEngineReady = useCallback((newEngine: GameEngine) => {
    setEngine(newEngine);
  }, []);

  return {
    engine,
    handleEngineReady,
    isReady: engine !== null,
  };
};

// ============================================================================
// Example usage component
// ============================================================================

export const GameCanvasExample: React.FC = () => {
  const { engine, handleEngineReady, isReady } = useGameEngine();

  const handleKeyPress = (key: string) => {
    if (!engine) return;

    // Example: Switch scenes with number keys
    switch (key) {
      case '1':
        engine.switchScene(GameScene.TITLE);
        break;
      case '2':
        engine.switchScene(GameScene.WORLD);
        break;
      case '3':
        engine.switchScene(GameScene.BATTLE);
        break;
    }
  };

  useEffect(() => {
    if (!isReady) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      handleKeyPress(e.key);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isReady]);

  return (
    <div style={{ width: '100vw', height: '100vh' }}>
      <GameCanvas onEngineReady={handleEngineReady} />
    </div>
  );
};
